import axios from 'axios';
import {
   API_ENDPOINTS,
   Button,
   ButtonTypes,
   Card,
   CardBody,
   CardData,
   customToast,
   Dropdown,
   FileHelpers,
   formatDate,
   Icons,
   PageLayoutPublic,
   replaceKeysWithValues,
   route,
   Spinner,
   StringHelpers,
   SubTitle,
   useDisabledContext,
   useLangContext,
   useStore,
   Variants,
} from 'carrier-fe';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HouseholderInstallationView } from '../../types/Registration/Journey';
import { STATUS_TYPES, SYSTEM_TYPES } from '../../util/Constants';
import WarrantyDetails from '../../components/WarrantyDetails';

interface LangPage {
   page: PageType;
   generic: LangGeneric;
}
interface LangGeneric {
   [key: string]: string;
   subcontractor: string;
   appliance: string;
   warranty_certificate: string;
   resend_warranty_certificate: string;
   resend_warranty_email: string;
   resend_warranty_prompt: string;
   resend_gassafe_email: string;
   download_gassafe_certificate: string;
}

interface PageType {
   installation_submitted_date: string;
   subcontracted: string;
   resend_gassafe_dialog_text: string;
   resend_warranty_dialog_text: string;
   resend_warranty_prompt_text: string;
   resend_warranty_email: string;
   gassafe_downloaded: string;
   warranty_downloaded: string;
   servicing_needed: string;
   system_details: string;
   appliance_x_of_y: any;
}

function Installation() {
   const { installationId } = useParams();

   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [pageLang, setPageLang] = useState<LangPage | null>(null);

   const { store, setValues } = useStore<HouseholderInstallationView>(
      {} as HouseholderInstallationView
   );
   const { crud, fields, getLangPage } = useLangContext();
   const { disabled } = useDisabledContext();
   const [warranty, setWarranty] = useState<any>();
   const navigate = useNavigate();

   useEffect(() => {
      const fetchPage = async () => {
         try {
            const data = await getLangPage('registration/viessmann-installation/view');
            setPageLang(data);
         } catch (err: any) {
            customToast({
               title: err?.response?.data?.message || '',
               variant: Variants.Danger,
            });
         }
      };

      fetchPage();
      fetchInstallationData();
   }, []);

   const fetchInstallationData = useCallback(async () => {
      setIsLoading(true);
      try {
         const { data } = await axios.get<{ data: HouseholderInstallationView }>(
            route(API_ENDPOINTS.HOUSEHOLDER.REGISTRATION.VIEW, {
               installationId: String(installationId),
            })
         );

         setValues(data.data);
      } catch (err: any) {
         customToast({
            title: err?.response?.data?.message || '',
            variant: Variants.Danger,
         });
      } finally {
         setIsLoading(false);
      }
   }, [installationId]);

   const downloadWarrantyCertificate = async () => {
      try {
         await FileHelpers.downloadFile(
            route(API_ENDPOINTS.HOUSEHOLDER.WARRANTY.CERTIFICATE, {
               installationId: installationId || '',
            }),
            'warrantyCertificate.pdf'
         );
         customToast({
            title:
               pageLang?.page?.warranty_downloaded ||
               'Warranty Certificate Downloaded Successfully',
            variant: Variants.Success,
         });
      } catch (error: any) {
         if (error.response && error.response.data) {
            const errorBlob = error.response.data;
            const errorText = await errorBlob.text();
            customToast({
               title: JSON.parse(errorText)?.message || '',
               variant: Variants.Danger,
            });
         } else {
            customToast({
               title: error?.message || 'An unexpected error occurred. Please try again.',
               variant: Variants.Danger,
            });
         }
      }
   };

   const getActions = (): DropdownItemProps[] => {
      if (!store) return [];

      let actions: DropdownItemProps[] = [];

      if (store?.status === STATUS_TYPES.COMPLETED && store?.active_warranty_exists) {
         actions.push({
            label: StringHelpers.title(
               pageLang?.generic.warranty_certificate || 'Warranty Certificate'
            ),
            onClick: downloadWarrantyCertificate,
            variant: Variants.Dark,
            icon: Icons.DOWNLOAD,
            disabled: disabled,
         });
      }

      if (
         store?.status === STATUS_TYPES.COMPLETED &&
         store?.system_type === SYSTEM_TYPES.GAS_BOILER
      ) {
         actions.push({
            label: StringHelpers.title(
               pageLang?.generic.download_gassafe_certificate || 'Download Gas Safe Certificate'
            ),
            onClick: downloadGasSafeCertificate,
            variant: Variants.Dark,
            icon: Icons.DOWNLOAD,
            disabled: disabled,
         });
      }

      return actions;
   };

   const downloadGasSafeCertificate = async () => {
      try {
         await FileHelpers.downloadFile(
            route(API_ENDPOINTS.HOUSEHOLDER.GASSAFE.CERTIFICATE, {
               installationId: installationId || '',
            }),
            'gasSafeCertificate.pdf'
         );
         customToast({
            title:
               pageLang?.page?.gassafe_downloaded || 'Gas Safe Certificate Downloaded Successfully',
            variant: Variants.Success,
         });
      } catch (error: any) {
         if (error.response && error.response.data) {
            const errorBlob = error.response.data;
            const errorText = await errorBlob.text();
            customToast({
               title: JSON.parse(errorText)?.message || '',
               variant: Variants.Danger,
            });
         } else {
            customToast({
               title: error?.message || 'An unexpected error occurred. Please try again.',
               variant: Variants.Danger,
            });
         }
      }
   };

   let actions = getActions();

   return (
      <PageLayoutPublic>
         <>
            {isLoading ? (
               <div className="d-flex justify-content-center align-items-center vh-100">
                  <Spinner />
               </div>
            ) : (
               <>
                  <div className="mb-4 d-flex justify-content-between align-items-center">
                     <Button
                        label={StringHelpers.title(crud?.buttons?.back.default || 'Back')}
                        onClick={() => navigate('/')}
                        variant={Variants.Dark}
                        type={ButtonTypes.Outline}
                        disabled={disabled}
                     />
                     {actions.length > 0 && (
                        <Dropdown
                           label={StringHelpers.title(crud?.buttons?.actions.default || 'Actions')}
                           items={getActions()}
                           type={ButtonTypes.Solid}
                           variant={Variants.Dark}
                           disabled={disabled}
                        />
                     )}
                  </div>

                  {/* Warranty */}
                  {store?.id && store?.status === STATUS_TYPES.COMPLETED && (
                     <WarrantyDetails
                        installation={store}
                        pageLang={pageLang}
                        onSuccess={fetchInstallationData}
                     />
                  )}

                  <Card>
                     <CardBody>
                        <SubTitle
                           className="mb-3 w-100"
                           style={{ color: '#464C5E' }}
                           title={StringHelpers.title(
                              crud?.models?.installation + ' ' + crud?.sub_titles?.details ||
                                 'Installation Details'
                           )}
                        />
                        <div className="row mb-4">
                           <div className="col-12 col-lg-6 col-xxl-4">
                              <div className="d-flex flex-column mb-3">
                                 <strong>
                                    {StringHelpers.title(fields?.regitered_by || 'Registered By')}
                                 </strong>
                                 <div>
                                    {StringHelpers.title(
                                       store?.customer_company?.primary_individual?.name ?? '-'
                                    )}
                                 </div>
                              </div>
                              <div className="d-flex flex-column mb-3">
                                 <strong>
                                    {StringHelpers.title(
                                       fields?.company_name || 'Installation company'
                                    )}
                                 </strong>
                                 <div>
                                    {StringHelpers.title(store?.customer_company?.name ?? '-')}
                                 </div>
                              </div>
                              <div className="d-flex flex-column mb-3">
                                 <strong>
                                    {StringHelpers.title(
                                       fields?.completion_date || 'Work completion date'
                                    )}
                                 </strong>
                                 <div>
                                    {StringHelpers.title(formatDate(store?.completion_date) || '-')}
                                 </div>
                              </div>
                              <div className="d-flex flex-column mb-3">
                                 <strong>
                                    {StringHelpers.title(
                                       pageLang?.page?.installation_submitted_date ||
                                          'Installation submitted date'
                                    )}
                                 </strong>
                                 <div>
                                    {StringHelpers.title(formatDate(store?.created_at) || '-')}
                                 </div>
                              </div>
                           </div>
                           <div className="col-12 col-lg-6 col-xxl-4">
                              <div className="d-flex flex-column mb-3">
                                 <strong>
                                    {StringHelpers.title(fields?.registered_by || 'Registered by')}
                                 </strong>
                                 <div>{store?.registered_by_name ?? '-'}</div>
                              </div>
                              <div className="d-flex flex-column mb-3">
                                 <strong>
                                    {StringHelpers.title(
                                       fields?.installation_postcode || 'Installation postcode'
                                    )}
                                 </strong>
                                 <div>{store?.postcode_zipcode?.toUpperCase() ?? '-'}</div>
                              </div>
                              <div className="d-flex flex-column mb-3">
                                 <strong>{StringHelpers.title(fields?.status || 'Status')}</strong>
                                 <div>{store?.status_display ?? '-'}</div>
                              </div>
                           </div>
                           <div className="col-12 col-lg-6 col-xxl-4">
                              <div className="d-flex flex-column mb-3">
                                 <strong>
                                    {StringHelpers.title(
                                       fields?.company_gas_safe_number || 'Gas Safe Number'
                                    )}
                                 </strong>
                                 <div>{store?.customer_company?.gas_safe_number ?? '-'}</div>
                              </div>
                           </div>
                        </div>
                     </CardBody>
                  </Card>

                  <Card>
                     <CardBody>
                        <SubTitle
                           className="mb-3 w-100"
                           style={{ color: '#464C5E' }}
                           title={StringHelpers.title(
                              crud?.models?.customer + ' ' + crud?.sub_titles?.details ||
                                 'Customer Details'
                           )}
                        />
                        <div className="row mb-4">
                           <div className="col-12 col-lg-6 col-xxl-4">
                              <CardData.ColumnItem
                                 title="Address Line 1"
                                 value={
                                    store?.customer_company?.primary_site?.address?.address_line_1
                                 }
                              />
                              <CardData.ColumnItem
                                 title="Address Line 2"
                                 value={
                                    store?.customer_company?.primary_site?.address?.address_line_2
                                 }
                              />
                              <CardData.ColumnItem
                                 title="Address Line 3"
                                 value={
                                    store?.customer_company?.primary_site?.address?.address_line_3
                                 }
                              />
                           </div>
                           <div className="col-12 col-lg-6 col-xxl-4">
                              <CardData.ColumnItem
                                 title="Town / City"
                                 value={store?.customer_company?.primary_site?.address?.town_city}
                              />
                              <CardData.ColumnItem
                                 title="State / County"
                                 value={
                                    store?.customer_company?.primary_site?.address?.state_county
                                 }
                              />
                              <CardData.ColumnItem
                                 title="Postcode / Zipcode"
                                 value={store?.customer_company?.primary_site?.address?.postcode_zipcode?.toUpperCase()}
                              />
                           </div>
                           <div className="col-12 col-lg-6 col-xxl-4">
                              <CardData.ColumnItem
                                 title="Country"
                                 value={store?.customer_company?.primary_site?.address?.country}
                              />
                              <CardData.ColumnItem
                                 title="New Build"
                                 value={
                                    store?.customer_company?.primary_site?.address
                                       ?.location_new_build
                                       ? 'Yes'
                                       : 'No'
                                 }
                              />
                           </div>
                        </div>
                     </CardBody>
                  </Card>

                  <Card>
                     <CardBody>
                        <SubTitle
                           className="mb-3 w-100"
                           style={{ color: '#464C5E' }}
                           title={StringHelpers.title(
                              pageLang?.page?.system_details || 'Appliance Details'
                           )}
                        />
                        <div className="mb-4">
                           <strong>
                              {StringHelpers.title(fields?.system_type || 'System type')}
                           </strong>
                           <div>{StringHelpers.title(store?.system_type_display || '-')}</div>
                        </div>

                        {store?.appliances?.map((appliance: any, index: number) => (
                           <Card key={index}>
                              <CardBody>
                                 <div className="mb-3">
                                    <strong>
                                       {replaceKeysWithValues(
                                          pageLang?.page?.appliance_x_of_y || '',
                                          { nth: index + 1, total: 1 }
                                       )}
                                    </strong>
                                 </div>
                                 <div className="row">
                                    <CardData.Column>
                                       <CardData.ColumnItem
                                          title="Serial Number"
                                          value={appliance?.serial_number}
                                       />
                                       <CardData.ColumnItem
                                          title="Name"
                                          value={appliance?.appliance_details?.full_name}
                                       />
                                    </CardData.Column>
                                    <CardData.Column>
                                       <CardData.ColumnItem
                                          title="Model"
                                          value={appliance?.appliance_details?.model}
                                       />
                                       <CardData.ColumnItem
                                          title="Type"
                                          value={appliance?.appliance_details?.appliance_type}
                                       />
                                    </CardData.Column>
                                    <CardData.Column>
                                       <CardData.ColumnItem
                                          title="Version"
                                          value={appliance?.appliance_details?.version}
                                       />
                                       <CardData.ColumnItem
                                          title="Installation Type"
                                          value={appliance?.appliance_details?.installation_type}
                                       />
                                    </CardData.Column>
                                 </div>
                              </CardBody>
                           </Card>
                        ))}
                     </CardBody>
                  </Card>
               </>
            )}
         </>
      </PageLayoutPublic>
   );
}

export default Installation;
